import { InjectionToken } from '@angular/core';
import { Observable } from 'rxjs';
import { FeatureFlagKey } from './feature-flag-key.enum';
import { IEvaluationOptions } from './evaluation-options.interface';
import { EvaluationContext, FeatureFlagVariant } from './feature-flag-types';

export const FEATURE_FLAG_SERVICE = new InjectionToken<IFeatureFlagService>('FEATURE_FLAG_SERVICE');

export interface IFeatureFlagService {
  /**
   * Initializes the service and returns an observable indicating whether the initialization was successful.
   * @returns An observable that emits a boolean indicating whether initialization was successful.
   */
  initialize$: () => Observable<boolean>;

  /**
   * Evaluates a feature flag for the current logged-in user and returns an observable with the feature flag variant
   * or null if the evaluation fails.
   * @param flagKey - The key of the feature flag to evaluate.
   * @param defaultValue - The default value to return if the feature flag is not found or cannot be evaluated.
   * @param options - Optional evaluation options (e.g., timeout settings).
   * @returns An observable that emits the feature flag variant for the current user, or null.
   */
  evaluate$: <TPayload = unknown>(
    flagKey: FeatureFlagKey,
    defaultValue: TPayload,
    options?: IEvaluationOptions
  ) => Observable<FeatureFlagVariant<TPayload> | null>;

  /**
   * Checks if a feature flag is enabled for the current logged-in user and returns an observable with a boolean value.
   * This is a convenience method that wraps evaluate$ and returns a simple boolean result.
   * If a value is not found, the value returned by default is `false`.
   * @param flagKey - The key of the feature flag to check.
   * @returns An observable that emits a boolean indicating whether the feature flag is enabled for the current user.
   */
  isEnabled$: (flagKey: FeatureFlagKey) => Observable<boolean>;

  /**
   * Retrieves a specific feature flag variant for any user specified in the evaluation context.
   * This method allows checking feature flags for users other than the current logged-in user.
   * @param context - The evaluation context containing the account ID of the user to evaluate.
   * @param flagKey - The key of the feature flag to retrieve.
   * @param defaultValue - The default value to return if the feature flag is not found or cannot be evaluated.
   * @returns An observable that emits the feature flag variant for the specified user or null if not found.
   */
  getVariant$: <TPayload = unknown>(
    context: EvaluationContext,
    flagKey: string,
    defaultValue?: TPayload
  ) => Observable<FeatureFlagVariant<TPayload> | null>;

  /**
   * Retrieves the anonymous ID for the current user.
   * @returns An observable that emits the anonymous ID for the current user.
   */
  anonymousId$: () => Observable<string>;

  /**
   * Tracks that a user has been exposed to a feature flag experiment.
   * This should be called when the user actually sees or interacts with the feature,
   * not just when the flag is evaluated.
   * @param flagKey - The key of the feature flag to track exposure for.
   * @param variant - The variant the user was exposed to.
   * @returns An observable that completes when the exposure has been tracked.
   */
  trackExposure$: (flagKey: FeatureFlagKey, variant: FeatureFlagVariant) => Observable<void>;
}
